import React, { useState, useEffect } from 'react';
import { ExternalLink, User, UserX, Link, Calendar, CalendarClock } from 'lucide-react';
import apiService from '../services/apiService';
import { useNavigate } from 'react-router-dom';
import { useToast } from './ToastProvider';
import { useI18n } from '../utils/i18n';

// Einheitliches Ticket-Kontextmenü (#120, W7). Hängt seit #138 direkt an der
// gemeinsamen ui/TicketCard (Prop enableContextMenu) und damit an ALLEN
// Karten-Listen (Dashboard, Kalender-Seitenleiste, Kanban) — gleiche Optionen,
// gleiche Reihenfolge, überall. Aktionen melden sich über onTicketUpdate UND
// das globale ticketsUpdated-Event, damit auch andere Ansichten nachziehen.
const TicketContextMenu = ({ ticket, position, onClose, onTicketUpdate, onOpenMerge }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const { t } = useI18n();

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const userResponse = await apiService.getCurrentUser();
        setCurrentUser(userResponse);
      } catch (error) {
        console.error('Error loading current user:', error);
      }
    };
    loadCurrentUser();
  }, []);

  const notifyUpdated = () => {
    try { onTicketUpdate && onTicketUpdate(); } catch (_) {}
    try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'ticketContextMenu', ticketId: ticket.id } })); } catch (_) {}
  };

  const handleOpenTicket = () => {
    try { window.dispatchEvent(new CustomEvent('openTicketDetailById', { detail: { ticketId: ticket.id } })); } catch (_) {}
    onClose && onClose();
  };

  const handleAssignToMe = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      await apiService.updateTicket(ticket.id, {
        assignedTo: currentUser.mail || currentUser.userPrincipalName
      });
      notifyUpdated();
      onClose();
    } catch (error) {
      console.error('Error assigning ticket to me:', error);
      pushToast({ message: t('contextMenu.assignError', 'Fehler beim Zuweisen des Tickets.'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAssignment = async () => {
    setLoading(true);
    try {
      await apiService.updateTicket(ticket.id, { assignedTo: '' });
      notifyUpdated();
      onClose();
    } catch (error) {
      if (error?.abgebrochen) return; // #554: Rueckfrage abgelehnt, nichts gespeichert
      console.error('Error removing ticket assignment:', error);
      pushToast({ message: t('contextMenu.unassignError', 'Fehler beim Entfernen der Zuweisung.'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const canFindInCalendar = !!(ticket && ticket.isPlanned && ticket.plannedStartDateTime && ticket.assignedTo);

  const handleFindInCalendar = () => {
    try {
      const params = new URLSearchParams();
      params.set('highlightTicketId', String(ticket.id));
      if (ticket.assignedTo) params.set('highlightUser', String(ticket.assignedTo));
      if (ticket.plannedStartDateTime) params.set('highlightStart', String(ticket.plannedStartDateTime));
      navigate(`/global-calendar?${params.toString()}`);
    } catch (_) {}
    onClose && onClose();
  };

  // #119 (W7): erster freier Arbeitszeit-Slot der nächsten Kalenderwoche;
  // ungeplante Tickets werden damit eingeplant (Server übernimmt die Suche)
  const handleMoveToNextWeek = async () => {
    setLoading(true);
    try {
      const resp = await apiService.planTicketNextWeek(ticket.id);
      const start = resp?.plannedStartDateTime ? new Date(resp.plannedStartDateTime) : null;
      const when = start
        ? start.toLocaleString([], { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
        : '';
      pushToast({ message: t('contextMenu.movedNextWeek', `Verschoben auf ${when}`, { date: when }), type: 'success' });
      notifyUpdated();
      onClose();
    } catch (error) {
      console.error('Error planning ticket for next week:', error);
      const serverMsg = error?.response?.data?.error;
      pushToast({ message: serverMsg || t('contextMenu.moveNextWeekError', 'Verschieben auf nächste Woche fehlgeschlagen.'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const isAssignedToMe = () => {
    if (!currentUser || !ticket.assignedTo) return false;
    const userEmail = currentUser.mail || currentUser.userPrincipalName;
    return ticket.assignedTo === userEmail;
  };

  const isPartOfProjectOrSubproject = () => !!(ticket.projectId || ticket.subprojectId);

  const menuItem = (icon, label, onClick, key) => (
    <div
      key={key}
      className="context-menu-item"
      onClick={onClick}
      style={{
        padding: '8px 16px',
        cursor: loading ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        opacity: loading ? 0.6 : 1
      }}
      onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)')}
      onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = 'transparent')}
    >
      {icon}
      {label}
    </div>
  );

  return (
    <div
      className="context-menu"
      style={{
        position: 'fixed',
        top: position.y,
        left: position.x,
        zIndex: 10000,
        backgroundColor: 'white',
        border: '1px solid var(--color-border-strong)',
        borderRadius: '4px',
        boxShadow: '0 2px 10px var(--color-shadow-10)',
        padding: '8px 0',
        minWidth: '220px'
      }}
    >
      {menuItem(<ExternalLink size={16} />, t('contextMenu.open', 'Öffnen'), handleOpenTicket, 'open')}
      {canFindInCalendar && menuItem(<Calendar size={16} />, t('contextMenu.findInCalendar', 'Im Kalender finden'), handleFindInCalendar, 'find')}
      {ticket.status !== 'abgeschlossen' && menuItem(<CalendarClock size={16} />, t('contextMenu.moveNextWeek', 'Auf nächste Woche verschieben'), handleMoveToNextWeek, 'nextweek')}
      {menuItem(<Link size={16} />, t('contextMenu.mergeInto', 'Zu einem Ticket hinzufügen'), (e) => { e.stopPropagation(); e.preventDefault(); if (onOpenMerge) onOpenMerge(ticket); onClose && onClose(); }, 'merge')}
      {!isPartOfProjectOrSubproject() && (
        isAssignedToMe()
          ? menuItem(<UserX size={16} />, t('contextMenu.unassign', 'Zuweisung entfernen'), handleRemoveAssignment, 'unassign')
          : menuItem(<User size={16} />, t('contextMenu.assignToMe', 'Mir zuweisen'), handleAssignToMe, 'assign')
      )}
    </div>
  );
};

export default TicketContextMenu;
